// interfaces / constants
import { IErrorResponse } from 'src/api/interfaces/errors'
import { NETWORK_ERROR } from 'src/constants/api_error_codes'

// actions
import { showSnackBar } from 'src/actions/app-state/app-state'

// helpers
import { textResources } from 'src/lang/de'
import { reportError } from 'src/utils/reporting/report-errors'
import { getStore } from 'src/store/store-holder'

import { CombinedGraphQLErrors } from '@apollo/client/errors'

export const formatApiErrorFullMessages = (error: IErrorResponse,
                                           separator: string = ', ',
                                           fallback: string = textResources.shared.errorUnknown): string =>
  error?.errors?.full_messages
    ? error.errors.full_messages.join(separator)
    : fallback

export const handleNetworkError = (error: Error) => {
  if (error.message === NETWORK_ERROR) {
    getStore().dispatch(showSnackBar({
      message: textResources.shared.genericErrorLong
    }))
  }

  throw error
}

const firstExtensions = (error: unknown): Record<string, unknown> | undefined =>
  CombinedGraphQLErrors.is(error) ? error.errors[0]?.extensions : undefined

const isFieldErrorMap = (value: unknown): value is Record<string, string[]> =>
  typeof value === 'object' && value !== null && !Array.isArray(value) &&
  Object.values(value).every((types) =>
    Array.isArray(types) && types.every((type) => typeof type === 'string'))

/**
 * The camelCased field -> error types map the backend sends alongside fullMessages
 * (`{ email: ['taken'] }`). The types are keys, not texts: use them to decide WHICH
 * field an error belongs to, and fullMessages for what to write next to it.
 *
 * The shape is checked rather than asserted, and a document that does not match is
 * reported: callers branch on the field names, so a format change would not throw here
 * but would silently stop putting messages next to their field — the quiet failure this
 * whole module exists to avoid. Follows `src/utils/authorization_reasons.ts`.
 */
export const getErrorDetailsGQL = (error: unknown): Record<string, string[]> | undefined => {
  const details = firstExtensions(error)?.details

  if (details === undefined) return undefined

  if (!isFieldErrorMap(details)) {
    reportError(new Error('GraphQL error details are not a field -> error types map'), { details })
    return undefined
  }

  return details
}

/**
 * The input field whose value the schema rejected before the resolver ran — a scalar
 * that did not coerce — or undefined when this is not such an error.
 *
 * Those errors carry `problems` and no `code`, where a validation failure from the
 * resolver carries `code` and `fullMessages`. Telling them apart matters because only
 * the resolver sends a sentence worth showing; for a coercion error the frontend has to
 * supply its own, and `problems[].path` says which field it belongs to instead of the
 * caller having to guess.
 *
 * Nothing else in the repository records this envelope — no fixture, no type, no second
 * consumer — so it is written down here. Measured by Maggie on 16.09.2026 against a local
 * backend, submitting `maggie@-kiel.de`, which passes the client regex and fails the
 * `Email` scalar:
 *
 * ```json
 * {"errors":[{"message":"Variable $input of type CreateUserInput! was provided invalid
 *   value for email (\"maggie@-kiel.de\" is not a valid email address)",
 *   "extensions":{"problems":[{"path":["email"],"explanation":"...","message":"..."}],
 *   "value":{...}}}]}
 * ```
 */
export const getSchemaCoercionFieldGQL = (error: unknown): string | undefined => {
  const extensions = firstExtensions(error)

  if (!extensions || 'code' in extensions || !Array.isArray(extensions.problems)) {
    return undefined
  }

  const path = (extensions.problems[0] as { path?: unknown } | undefined)?.path

  return Array.isArray(path) && typeof path[0] === 'string' ? path[0] : undefined
}

// TODO: Move to lopo-api
export const getFirstFullMessageGQL = (error: unknown): string | undefined => {
  if (
    CombinedGraphQLErrors.is(error) &&
    error.errors[0]?.extensions &&
    'fullMessages' in error.errors[0].extensions &&
    Array.isArray(error.errors[0]?.extensions.fullMessages)
  ) {
    return error.errors[0]?.extensions.fullMessages[0]
  }

  return undefined
}
